// Utility functions for markdown rendering
// Using react-markdown which handles the unified/remark/rehype pipeline internally

/**
 * Custom components for react-markdown
 */
export const markdownComponents = {
  // Code blocks with copy button functionality
  code: ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    if (inline) {
      return (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    }

    return (
      <div className="code-block-wrapper">
        {language && <div className="code-language">{language}</div>}
        <pre className={className}>
          <code {...props}>{children}</code>
        </pre>
      </div>
    );
  },

  // Custom link handling
  a: ({ node, children, href, ...props }) => {
    // Open external links in new tab
    const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));

    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  },

  // Add anchor links to headings
  h1: ({ node, children, ...props }) => {
    const id = generateHeadingId(children);
    return <h1 id={id} {...props}>{children}</h1>;
  },
  h2: ({ node, children, ...props }) => {
    const id = generateHeadingId(children);
    return <h2 id={id} {...props}>{children}</h2>;
  },
  h3: ({ node, children, ...props }) => {
    const id = generateHeadingId(children);
    return <h3 id={id} {...props}>{children}</h3>;
  },
  h4: ({ node, children, ...props }) => {
    const id = generateHeadingId(children);
    return <h4 id={id} {...props}>{children}</h4>;
  },

  // Custom image handling
  img: ({ node, src, alt, ...props }) => {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        {...props}
      />
    );
  },

  // Tables
  table: ({ node, children, ...props }) => {
    return (
      <div className="table-wrapper">
        <table {...props}>{children}</table>
      </div>
    );
  }
};

/**
 * Generates a URL-friendly ID from heading text
 */
function generateHeadingId(children) {
  if (!children) return '';

  const text = typeof children === 'string'
    ? children
    : Array.isArray(children)
      ? children.join('')
      : String(children);

  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Extracts plain text from React children (for search, headings, etc.)
 */
export function extractText(children) {
  if (typeof children === 'string') {
    return children;
  }

  if (Array.isArray(children)) {
    return children.map(extractText).join('');
  }

  if (children && typeof children === 'object' && children.props) {
    return extractText(children.props.children);
  }

  return '';
}
